<template>
  <v-row>
    <v-col>
      <v-data-table
        :headers="headers"
        :items="disputes"
        :server-items-length="totalDisputes"
        :loading="disputesLoading"
        hide-default-footer
        :multi-sort="false"
        :must-sort="true"
        @page-count="pageCount = $event"
        :page.sync="page"
        :options.sync="options"
        :items-per-page="itemsPerPage"
        :footer-props="{ showFirstLastPage: true }"
        class="ic-table elevation-0 ic-table--heavy-headers"
      >
        <template v-slot:top>
          <v-container fluid class="white header-container">
            <v-row align="center" dense justify="center">
              <v-col cols="12" md="2" class="v-toolbar__title">
                {{ $t("pages.dispute.list.title") }}
              </v-col>
              <v-col cols="12" md="10">
                <div class="d-flex flex-wrap justify-end toolbar-items">
                  <v-text-field
                    class="toolbar-item"
                    v-model="dynamicSearch"
                    append-icon="$icons_system_search"
                    :label="$t('misc.search')"
                    single-line
                    clearable
                    dense
                    outlined
                    hide-details
                    style="max-width: 184px;"
                  ></v-text-field>
                  <v-select class="toolbar-item date-type-picker"
                      :items="dateTypes"
                      hide-details
                      outlined
                      dense
                      value="refundDate"
                      v-model="dateType"
                      style="max-width: 224px;"
                  ></v-select>
                  <div class="toolbar-item dispute-date-picker" style="max-width: 284px;">
                    <v-icon class="date-picker-icon-calendar">$icons_product_calendar</v-icon>
                    <date-picker
                      :label="$t('pages.refund.list.startDate')"
                      :locale="selectedLanguage"
                      :displayFormat="$t('pages.refund.list.dateFormat')"
                      v-model="startDate"
                      :max="endDate"
                    ></date-picker>
                    <v-icon class="date-picker-icon-arrow">$icons_system_arrow</v-icon>
                    <date-picker
                      :label="$t('pages.refund.list.endDate')"
                      :locale="selectedLanguage"
                      :displayFormat="$t('pages.refund.list.dateFormat')"
                      v-model="endDate"
                      :min="startDate"
                    ></date-picker>
                  </div>
                  <v-btn
                    color="primary"
                    :loading="reportGenerating"
                    :disabled="disableDownload"
                    @click="downloadReport()"
                    class="toolbar-item header-list-button"
                  >
                    <span>{{ $t("pages.dispute.list.download") }}</span>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <div class="header-separator"></div>
        </template>
        <template v-slot:header.transaction.date="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.disputeDate="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.payout.arrivalDate="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.evidence.dueDate="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.transaction.card.brand="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.booking.dispatchId="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.transaction.applicationFee="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>

        <template v-slot:header.booking.amount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.status="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.amount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.fee="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.reason="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>

        <template v-slot:item.transaction.date="{ item }">
          <span>{{ formatDate(item.transaction.date) }}</span>
        </template>
        <template v-slot:item.disputeDate="{ item }">
          <span>{{ formatDate(item.disputeDate) }}</span>
        </template>
        <template v-slot:item.evidence.dueDate="{ item }">
          <span>{{ formatDate(item.evidence.dueDate) }}</span>
        </template>
        <template v-slot:item.payout.arrivalDate="{ item }">
          <span>{{ formatDate(item.payout && item.payout.arrivalDate) }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <div class="d-flex">
            <v-icon left color="primary" v-if="evidenceUnderReview(item.status)">mdi-check</v-icon>
            <v-icon left color="primary" v-if="item.status === 'won'">mdi-thumb-up</v-icon>
            <v-icon left color="primary" v-if="item.status === 'warning_closed'">mdi-thumb-up</v-icon>
            <v-icon left color="primary" v-if="item.status === 'lost'">mdi-thumb-down</v-icon>
            <v-icon left color="primary" v-if="evidenceEditable(item.status)">mdi-flag</v-icon>
            <v-icon left color="primary" v-if="item.status === 'charge_refunded'">mdi-cash-refund</v-icon>
            {{ item.status }}
          </div>
        </template>

        <template v-slot:item.transaction.card.brand="{ item }">
              <span>
                <v-icon>{{ creditCardIcon(item.transaction.card.brand) }}</v-icon>
              </span>
        </template>
        <template v-slot:item.action="{ item }" v-if="hasAtLeastOneAccess(['fleetAdmin', 'paymentManager'])">
          <v-dialog
            :key="item.id"
            v-model="dialogs[item.id]"
            :internal-activator="true"
            persistent
            max-width="550"
          >
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" color="primary" small>{{ $t(viewEvidenceButtonText(item.status)) }}</v-btn>
            </template>
            <v-card>
              <v-card-title class="headline">{{
                $t("pages.dispute.list.dialog.title")
                }}</v-card-title>
              <v-card-text>
                <v-textarea
                  name="input-7-1"
                  auto-grow
                  outlined
                  :readonly="!evidenceEditable(item.status)"
                  v-model="item.evidence.details.text"
                ></v-textarea>
                <div v-if="evidenceEditable(item.status)">{{ $t("pages.dispute.list.dialog.automaticallySubmitted",  { date: formatDate(item.evidence.dueDate) }) }}</div>
                <div v-if="evidenceUnderReview(item.status)">{{ $t("pages.dispute.list.dialog.disputeUnderReview") }}</div>
              </v-card-text>
              <v-card-actions>
                <v-chip
                  v-if="item.evidence.details.file !== null"
                  @click="downloadEvidence(item.id, item.booking.dispatchId)"
                  class="ma-2"
                  color="blue lighten-5"
                  text-color="blue darken-1"
                  label
                >
                  <v-avatar left>
                    <v-progress-circular
                      v-if="evidenceLoading"
                      :size="20"
                      :width="2"
                      indeterminate
                    ></v-progress-circular>
                    <v-icon v-else>mdi-paperclip</v-icon>
                  </v-avatar>
                  {{ evidenceFilename(item.booking.dispatchId) }}
                </v-chip>

                <div class="flex-grow-1"></div>
                <v-btn
                  v-if="evidenceEditable(item.status)"
                  color="primary"
                  text
                  @click="$set(dialogs, item.id, false)"
                >{{ $t("pages.dispute.list.dialog.cancel") }}</v-btn
                >
                <v-btn v-else color="primary" @click="$set(dialogs, item.id, false)">{{
                  $t("pages.dispute.list.dialog.ok")
                  }}</v-btn>

                <v-btn
                  v-if="evidenceEditable(item.status)"
                  color="primary"
                  @click="submitEvidence(item)"
                >{{ $t("pages.dispute.list.dialog.submit") }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
      <div class="text-center pt-8">
        <v-pagination v-model="page" :length="pageCount" total-visible="10"></v-pagination>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import debounce from 'debounce';
import moment from 'moment';
import dayjs from '../plugins/dayjs';
import DatePicker from './DatePicker.vue';
import { creditCardIcon } from '../mixins/creditCardIcons.mixins';
import { accesses } from '../mixins/authentication.mixins';

export default {
  name: 'disputeList',
  components: { DatePicker },
  mixins: [
    accesses,
    creditCardIcon,
  ],
  props: {
    evidenceLoading: {
      type: Boolean,
      required: true,
    },
    disputesLoading: {
      type: Boolean,
      required: true,
    },
    disputes: {
      type: Array,
      required: true,
    },
    totalDisputes: {
      type: Number,
      required: true,
    },
    selectedLanguage: {
      type: String,
      required: true,
    },
    reportGenerating: {
      type: Boolean,
      required: true,
    },
  },
  created() {
    this.debouncedLoad = debounce(this.load, 500);
  },
  data() {
    return {
      screenName: 'dispute-list',
      options: {
        sortBy: ['disputeDate'], sortDesc: [true],
      },
      dialogs: {},
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      loading: false,
      dateType: 'disputeDate',
      startDate: dayjs()
        .subtract(31, 'days')
        .format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      dynamicSearch: '',
    };
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
    totalDisputes() {
      this.options.pageCount = Math.round(this.totalDisputes / this.itemsPerPage) + 1;
    },
    startDate() {
      this.load();
    },
    endDate() {
      this.load();
    },
    dateType() {
      this.load();
    },
    dynamicSearch() {
      this.page = 1;
      this.debouncedLoad();
    },
  },
  computed: {
    dateTypes() {
      return [
        { text: this.$t('pages.dispute.filters.disputeDate'), value: 'disputeDate' },
        { text: this.$t('pages.dispute.filters.transactionDate'), value: 'transactionDate' },
        { text: this.$t('pages.dispute.filters.payoutDate'), value: 'payoutDate' },
        { text: this.$t('pages.dispute.filters.respondByDate'), value: 'respondByDate' },
      ];
    },
    disableDownload() {
      return false;
    },
    startDateForRequest() {
      return `${this.startDate}T00:00:00Z`;
    },
    endDateForRequest() {
      return `${this.endDate}T23:59:59Z`;
    },
    headers() {
      return [
        {
          text: this.$t('pages.dispute.list.bookingChannelName'),
          align: 'left',
          sortable: true,
          value: 'booking.bookingChannelName',
        },
        {
          text: this.$t('pages.dispute.list.disputeDate'),
          align: 'left',
          sortable: true,
          value: 'disputeDate',
        },
        {
          text: this.$t('pages.dispute.list.transactionDate'),
          align: 'left',
          sortable: true,
          value: 'transaction.date',
        },
        {
          text: this.$t('pages.dispute.list.bookingId'),
          align: 'left',
          sortable: true,
          value: 'booking.dispatchId',
        },
        {
          text: this.$t('pages.dispute.list.cardBrand'),
          align: 'left',
          sortable: true,
          value: 'transaction.card.brand',
        },
        {
          text: this.$t('pages.dispute.list.fareAmount'),
          align: 'left',
          sortable: true,
          value: 'booking.amount',
        },
        {
          text: this.$t('pages.dispute.list.applicationFee'),
          align: 'left',
          sortable: true,
          value: 'transaction.applicationFee',
        },
        {
          text: this.$t('pages.dispute.list.payoutDate'),
          align: 'left',
          sortable: true,
          value: 'payout.arrivalDate',
        },

        {
          text: this.$t('pages.dispute.list.disputeFee'),
          align: 'left',
          sortable: true,
          value: 'fee',
        },
        {
          text: this.$t('pages.dispute.list.disputeAmount'),
          align: 'left',
          sortable: true,
          value: 'amount',
        },
        {
          text: this.$t('pages.dispute.list.disputeStatus'),
          align: 'left',
          sortable: true,
          value: 'status',
        },
        {
          text: this.$t('pages.dispute.list.disputeReason'),
          align: 'left',
          sortable: true,
          value: 'reason',
        },
        {
          text: this.$t('pages.dispute.list.disputeRespondByDate'),
          align: 'left',
          sortable: true,
          value: 'evidence.dueDate',
        },
        {
          text: '',
          value: 'action',
          width: '10%',
          sortable: false,
          align: 'end',
        },
      ];
    },
  },
  methods: {
    evidenceUnderReview(status) {
      return (status === 'under_review' || status === 'warning_under_review');
    },
    evidenceEditable(status) {
      return (status === 'needs_response' || status === 'warning_needs_response');
    },
    viewEvidenceButtonText(status) {
      if (this.evidenceEditable(status)) return 'pages.dispute.list.review';
      return 'pages.dispute.list.view';
    },
    evidenceFilename(dispatchId) {
      return `evidence-${dispatchId}.pdf`;
    },
    async load() {
      if (moment(this.endDate).diff(moment(this.startDate), 'days') > 31) {
        this.$store.dispatch('globalMessages/addError',
          { message: 'lists.dateRangeError' });
        return;
      }
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'disputeDate';
      this.$emit('loadData', {
        page: page - 1,
        startDate: this.startDateForRequest,
        endDate: this.endDateForRequest,
        dateType: this.dateType,
        maxPerPage: itemsPerPage,
        sort: sortDesc[0] ? `-${sort}` : sort,
        dynamicSearch: this.dynamicSearch,
      });
    },
    downloadEvidence(disputeId, dispatchId) {
      this.$emit('downloadEvidence', {
        disputeId,
        evidenceFilename: this.evidenceFilename(dispatchId),
      });
    },
    downloadReport() {
      const { sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'disputeDate';
      this.$emit('downloadReport', {
        startDate: this.startDateForRequest,
        endDate: this.endDateForRequest,
        dateType: this.dateType,
        sort: sortDesc[0] ? `-${sort}` : sort,
      });
    },
    submitEvidence(item) {
      this.$set(this.dialogs, item.id, false);
      this.$emit('submitEvidence', {
        disputeId: item.id,
        providerInstance: item.provider.instance,
        comment: item.evidence.details.text,
      });
    },
    formatDate(date) {
      if (!date) return null;
      return dayjs(date).utc().format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>

<style lang="scss">
.toolbar-items {
  .toolbar-item:not(:first-child) {
    margin-left: 8px;
    margin-bottom: 8px;
  }
}
.dispute-date-picker {
  display: flex;
  flex-flow: row;
  border: 1px #a1a1a1 solid;
  border-radius: 5px;
  padding: 3px 12px;
  max-height: 40px;
  align-items: center;
}

.mobile-title-dispute {
  display: flex;
  justify-content: space-between;

  .header-list-button {
    padding: 0 !important;
    min-width: 45px !important;
  }
}

.dispute-header-menu {
  display: flex;
  flex-flow: row;
  align-items: center;
}

.date-picker-icon-arrow {
  min-width: 20px;
  min-height: 20px;
}

.date-picker-icon-calendar {
  width: 30px;
  height: 30px;
  min-width: 20px;
  min-height: 20px;
}

@media only screen and (max-width: 1273px) {
  .date-picker-icon-calendar {
    min-width: 20px;
    min-height: 20px;
    width: 20px;
    height: 20px;
  }
}

@media only screen and (max-width: 959px) {
  .dispute-header-menu-input {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}
</style>
